import { createReducer, on } from '@ngrx/store';
import { SadmClient, SadmEntityResponse } from '@pixacare/dtos';
import { Form, ModuleId } from '@pixacare/forms';
import { toArchivable } from '../archivable.adapter';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { toStoreSadmEntity } from './adapters/store-sadm-entity.adapter';
import { StoreQuickModule } from './models/store-quick-module';
import { sadmActions } from './sadm.actions';
import { initialSadmState, SadmState } from './sadm.state';

const formToQuickModule = (form: Form, sadmClientId: number): SadmState['quickModules'] =>
  (form.quickModules || []).reduce(
    (acc, module) => {
      acc[module.moduleId] = {
        ...module,
        sadmClientId,
      };
      return acc;
    },
    {} as Record<ModuleId, StoreQuickModule>,
  );

const mergeSadmClients = (
  currentClients: SadmState['clients'],
  sadmClients: SadmClient[],
  override: boolean,
): SadmState['clients'] => {
  const clients = { ...currentClients };

  sadmClients.forEach((sadmClient) => {
    if (override || !clients[sadmClient.id]) {
      clients[sadmClient.id] = sadmClient;
    }
  });

  return clients;
};

const mergeSadmEntities = (
  currentEntities: SadmState['entities'],
  sadmEntities: SadmEntityResponse[],
): SadmState['entities'] => {
  const entities = { ...currentEntities };

  sadmEntities.forEach((sadmEntity) => {
    entities[sadmEntity.id] = {
      ...entities[sadmEntity.id],
      ...toStoreSadmEntity(sadmEntity),
    };
  });

  return entities;
};

const mergeQuickModules = (
  currentQuickModules: SadmState['quickModules'],
  sadmClients: SadmClient[],
): SadmState['quickModules'] => {
  const quickModules = { ...currentQuickModules };

  sadmClients.forEach(({ id, analysisCreationForm, entityCreationForm }) => {
    Object.assign(
      quickModules,
      analysisCreationForm ? formToQuickModule(analysisCreationForm, id) : {},
      entityCreationForm ? formToQuickModule(entityCreationForm, id) : {},
    );
  });

  return quickModules;
};

export const sadmReducer = createReducer(
  initialSadmState,

  on(lifeCycleActions.resetState, () => initialSadmState),

  on(sadmActions.getSadmClientsSuccess, (state: SadmState, { sadmClients, override }) => ({
    ...state,
    clients: mergeSadmClients(state.clients, sadmClients, override),
    userSadmClients: override ? sadmClients.map((sadmClient) => sadmClient.id) : state.userSadmClients,
  })),

  on(sadmActions.setSadmClientsQuickModules, (state: SadmState, { sadmClients }) => ({
    ...state,
    quickModules: mergeQuickModules(state.quickModules, sadmClients),
  })),

  on(sadmActions.getSadmEntitiesSuccess, (state: SadmState, { clientCode, patientId, sadmEntities }) => ({
    ...state,
    entities: mergeSadmEntities(state.entities, sadmEntities),
    loadState: {
      ...state.loadState,
      [clientCode]: {
        ...state.loadState[clientCode],
        [patientId]: true,
      },
    },
  })),

  on(sadmActions.getSadmEntitySuccess, sadmActions.updateSadmEntitySuccess, (state: SadmState, { sadmEntity }) => ({
    ...state,
    entities: {
      ...state.entities,
      [sadmEntity.id]: {
        ...state.entities[sadmEntity.id],
        ...toStoreSadmEntity(sadmEntity),
      },
    },
  })),

  on(sadmActions.unloadSadmEntity, (state: SadmState, { sadmEntityId }) => {
    const entities = { ...state.entities };
    delete entities[sadmEntityId];

    return { ...state, entities };
  }),

  on(
    sadmActions.archiveSadmEntitySuccess,
    sadmActions.unarchiveSadmEntitySuccess,
    (state: SadmState, { sadmEntity }) => ({
      ...state,
      entities: {
        ...state.entities,
        [sadmEntity.id]: {
          ...state.entities[sadmEntity.id],
          ...toArchivable(sadmEntity),
        },
      },
    }),
  ),
);
